import { useState } from "react";
import { Copy, Facebook, Mail, MessageCircle, Check, Share2 } from "lucide-react";
import { useTranslation } from "@/i18n";

/** Reusable share bar. `url` is the permanent story URL; `title` is used as
 *  the share headline; `text` is a longer description for email/WhatsApp.
 *  `crawlerUrl`, when supplied, is used for Facebook + Twitter share links so
 *  their server-side crawlers hit a backend HTML endpoint that emits
 *  per-story Open Graph + Twitter meta tags (the SPA can't, since FB/Twitter
 *  don't execute JavaScript). */
export default function ShareButtons({ url, title, text, crawlerUrl }) {
  const [t] = useTranslation();
  const [copied, setCopied] = useState(false);
  const [hint, setHint] = useState(""); // user-facing toast after FB click
  const pageUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  // ── Facebook OG cache buster ──────────────────────────────────────
  // Facebook caches link previews per URL, and even after we update
  // the OG tags it still serves a stale preview if it has seen the
  // exact URL within the last ~30 days. To force a guaranteed-fresh
  // scrape on every share click, append a per-mount random token to
  // the URL. The backend's OG renderer reads the full request URL
  // and echoes it as og:url, so FB can't canonicalise back to a
  // stale entry either. Daily granularity (`v=YYYYMMDD`) wasn't
  // enough — multiple shares on the same day reused FB's cache.
  //
  // Computed once per mount via lazy useState (React's blessed escape
  // hatch for non-pure init: `useState(fn)` runs `fn` exactly once on
  // first render). Wrapping Math.random()/new Date() in useMemo
  // upsets the `react-hooks/purity` rule because the *closure* is
  // observably impure — but a one-shot init via useState is fine.
  const [cacheBust] = useState(() => {
    const d = new Date();
    const day = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    const rand = Math.random().toString(36).slice(2, 8);
    return `v=${day}-${rand}`;
  });
  const base = crawlerUrl || pageUrl;
  const ogUrl = !base
    ? ""
    : base.includes("?")
      ? `${base}&${cacheBust}`
      : `${base}?${cacheBust}`;
  const shareTitle = title || "A Storylo story";
  const shareText = text || shareTitle;

  const enc = encodeURIComponent;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc(ogUrl)}`;
  const tw = `https://twitter.com/intent/tweet?url=${enc(ogUrl)}&text=${enc(shareTitle)}`;
  // WhatsApp's link preview is also scraper-driven, so use the OG URL.
  // The api/share endpoint redirects humans to /s/{slug} via meta refresh
  // immediately on click, so the recipient still lands on the clean URL.
  const wa = `https://wa.me/?text=${enc(shareText + "\n\n" + ogUrl)}`;
  // Email previews on most clients (Apple Mail, Gmail) also scrape OG.
  const email = `mailto:?subject=${enc(shareTitle)}&body=${enc(shareText + "\n\n" + ogUrl)}`;

  // Web Share API — uses the OS native share sheet on MOBILE (iOS /
  // Android), which routes correctly into the Facebook / WhatsApp /
  // Messenger / email apps. On DESKTOP we deliberately avoid it: Windows'
  // system share panel lists app icons (incl. a Facebook tile) that depend
  // on Store apps being installed — clicking them frequently does nothing.
  const isMobileDevice =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  const canNativeShare =
    isMobileDevice &&
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const onPrimaryShare = async () => {
    setHint("");
    if (canNativeShare) {
      try {
        // Use the OG-aware URL so that whichever app the OS user picks
        // (FB, WhatsApp, iMessage, Messenger, X, etc.) scrapes the
        // backend endpoint that emits per-story OG tags — not the
        // SPA shell which only has generic site-wide tags.
        await navigator.share({ title: shareTitle, text: shareText, url: ogUrl });
      } catch (_e) { /* user cancelled — that's fine */ }
      return;
    }
    // Desktop fallback — show the icon row hint so they know what to do next.
    setHint("Pick where to share below.");
    setTimeout(() => setHint(""), 4000);
  };

  // Let the plain anchor (target=_blank) do the navigation — popup
  // blockers and ad-blockers routinely kill window.open() popups to
  // facebook.com (and window.open with "noopener" returns null BY SPEC,
  // which used to trigger a second, always-blocked open → "nothing
  // happens" when clicking the icon). A direct user-gesture anchor click
  // is never blocked. We only attach a helpful hint on click.
  const openShare = (url, label) => () => {
    if (label === "facebook") {
      setHint("Facebook opened in a new tab. Click \"Post to Facebook\" inside it to share.");
      setTimeout(() => setHint(""), 6000);
    }
  };

  const onCopy = async () => {
    try {
      // Copy the crawler-readable URL so pasting into Facebook/WhatsApp
      // shows the story thumbnail (humans get redirected to the pretty URL).
      await navigator.clipboard.writeText(ogUrl || pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* ignored */ }
  };

  const iconBtn = "inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors";

  return (
    <div className="flex flex-col gap-2 w-full" data-testid="share-buttons">
      <div className="flex items-center flex-wrap gap-2">
        {/* Primary share button — opens the OS native share sheet on
            mobile, otherwise just sets a desktop hint. */}
        <button
          onClick={onPrimaryShare}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[color:var(--accent)] text-black text-sm font-medium hover:opacity-90 transition-opacity"
          data-testid="share-primary"
        >
          <Share2 className="w-4 h-4" />
          {canNativeShare ? t("share") : t("share")}
        </button>
        <span className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] ml-1">
          or
        </span>
        <button onClick={onCopy} className={iconBtn} title={copied ? t("copied") : t("copy_link")} data-testid="share-copy" aria-label={t("copy_link")}>
          {copied ? <Check className="w-4 h-4 text-[color:var(--accent)]" /> : <Copy className="w-4 h-4" />}
        </button>
        <a href={fb} onClick={openShare(fb, "facebook")} target="_blank" rel="noopener noreferrer" className={iconBtn} title={t("share_facebook")} data-testid="share-facebook" aria-label={t("share_facebook")}>
          <Facebook className="w-4 h-4" />
        </a>
        <a href={tw} onClick={openShare(tw, "twitter")} target="_blank" rel="noopener noreferrer" className={iconBtn} title={t("share_twitter")} data-testid="share-twitter" aria-label={t("share_twitter")}>
          <span className="font-bold text-sm leading-none">𝕏</span>
        </a>
        <a href={wa} onClick={openShare(wa, "whatsapp")} target="_blank" rel="noopener noreferrer" className={iconBtn} title={t("share_whatsapp")} data-testid="share-whatsapp" aria-label={t("share_whatsapp")}>
          <MessageCircle className="w-4 h-4" />
        </a>
        <a href={email} className={iconBtn} title={t("share_email")} data-testid="share-email" aria-label={t("share_email")}>
          <Mail className="w-4 h-4" />
        </a>
      </div>
      {hint && (
        <div className="text-[11px] text-[color:var(--muted)] leading-relaxed" data-testid="share-hint">
          {hint}
        </div>
      )}
    </div>
  );
}
